/**
 * AttachEmailDialog — search-and-pick UI for manually attaching an email
 * the auto-matcher missed to a job (CSR command-centre Slice 1, Phase D —
 * see docs/features/csr-command-centre/). Opened from JobCommsSection's
 * "Attach an email" control.
 *
 * Searches the job's customer's emails via the same read the
 * Communications page uses (`DataAdapter.listCommunications`, scoped by
 * `customerId` + `types: ["email"]`) — no bespoke backend surface. Picking
 * a result calls back to the caller (`onAttach`), which owns the actual
 * `linkEmailToJob` write and the optimistic-insert/rollback pattern shared
 * with JobCommsSection's other actions.
 *
 * Emails already shown on this job can't be excluded precisely: the
 * composed job-comms view deliberately never exposes the underlying
 * email id (see api-types.ts's `EmailJobLinkView` docstring, sourced from
 * data/api/jobComms.ts — actions there take the *link* id, not the email
 * id), so there is no shared
 * identity to filter on. Instead, a candidate whose sender+subject match
 * a row already on this job is marked (not hidden) via `alreadyLinkedKeys`
 * — a best-effort dedup hint, not a guarantee.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../primitives/command";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CommunicationItem } from "@shared-core/mock-data/communications";

export interface EmailCandidate {
  id: string;
  direction: "inbound" | "outbound";
  from_name: string | null;
  from_address: string;
  subject: string;
  snippet: string;
  sent_at: string;
}

/** Identity key used for the best-effort "already on this job" marking —
 *  see file header. */
export function emailIdentityKey(fromAddress: string, subject: string): string {
  return `${fromAddress.trim().toLowerCase()}|||${subject.trim().toLowerCase()}`;
}

interface AttachEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The job's customer — scopes the search to one customer's emails. */
  customerId: string;
  /** Identity keys (see `emailIdentityKey`) of emails already shown on
   *  this job, for the "already attached" marker. */
  alreadyLinkedKeys: Set<string>;
  /** Performs the actual attach write. The dialog awaits this before
   *  closing so it can show an "Attaching…" state on the picked row. */
  onAttach: (candidate: EmailCandidate) => Promise<void>;
  /** Pre-seeds the "attaching" row for Storybook — not used on the page. */
  initialAttachingId?: string;
}

const SEARCH_DEBOUNCE_MS = 250;
const RESULTS_PAGE_SIZE = 20;

function isEmailItem(
  item: CommunicationItem,
): item is CommunicationItem & { type: "email" } {
  return item.type === "email";
}

function formatCandidateDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function AttachEmailDialog({
  open,
  onOpenChange,
  customerId,
  alreadyLinkedKeys,
  onAttach,
  initialAttachingId,
}: AttachEmailDialogProps) {
  const dataAdapter = useDataAdapter();
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<EmailCandidate[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachingId, setAttachingId] = useState<string | null>(
    initialAttachingId ?? null,
  );
  const fetchSeq = useRef(0);

  const runSearch = useCallback(
    async (q: string) => {
      const seq = ++fetchSeq.current;
      setLoading(true);
      setError(null);
      try {
        const result = await dataAdapter.listCommunications({
          customerId,
          types: ["email"],
          q: q || undefined,
          pageSize: RESULTS_PAGE_SIZE,
        });
        if (seq !== fetchSeq.current) return;
        setCandidates(
          result.items.filter(isEmailItem).map((item) => ({
            id: item.data.id,
            direction: item.data.direction,
            from_name: item.data.from_name || null,
            from_address: item.data.from_address,
            subject: item.data.subject,
            snippet: item.data.snippet,
            sent_at: item.data.sent_at,
          })),
        );
      } catch (err) {
        if (seq !== fetchSeq.current) return;
        setError(err instanceof Error ? err.message : "Search failed");
        setCandidates([]);
      } finally {
        if (seq === fetchSeq.current) setLoading(false);
      }
    },
    [dataAdapter, customerId],
  );

  // Reset and load the customer's most recent emails as soon as the dialog opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setCandidates(null);
    setAttachingId(initialAttachingId ?? null);
    void runSearch("");
    // Only on open — the debounced effect below handles subsequent typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced re-search while the dialog stays open and the query changes.
  useEffect(() => {
    if (!open || candidates === null) return;
    const handle = setTimeout(
      () => void runSearch(query.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function handleSelect(candidate: EmailCandidate) {
    if (attachingId) return;
    setAttachingId(candidate.id);
    try {
      await onAttach(candidate);
    } finally {
      setAttachingId(null);
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 overflow-hidden sm:max-w-lg">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle>Attach an email</DialogTitle>
          <DialogDescription>
            Search this customer&apos;s emails and pick one to link to this job.
          </DialogDescription>
        </DialogHeader>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by subject, sender, or body…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-[360px]">
            {loading && (
              <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Searching…
              </div>
            )}
            {error && !loading && (
              <div className="px-4 py-6 text-sm text-destructive">{error}</div>
            )}
            {!loading && !error && candidates && candidates.length === 0 && (
              <CommandEmpty>
                No emails found{query.trim() ? ` for "${query.trim()}"` : ""}.
              </CommandEmpty>
            )}
            {!loading && !error && candidates && candidates.length > 0 && (
              <CommandGroup>
                {candidates.map((candidate) => {
                  const alreadyLinked = alreadyLinkedKeys.has(
                    emailIdentityKey(candidate.from_address, candidate.subject),
                  );
                  const isAttaching = attachingId === candidate.id;
                  return (
                    <CommandItem
                      key={candidate.id}
                      value={candidate.id}
                      disabled={alreadyLinked || attachingId !== null}
                      onSelect={() => {
                        if (alreadyLinked) return;
                        void handleSelect(candidate);
                      }}
                      className="flex-col items-stretch gap-0.5 py-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate">
                          {candidate.from_name || candidate.from_address}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatCandidateDate(candidate.sent_at)}
                        </span>
                      </div>
                      <span className="text-sm truncate">
                        {candidate.subject}
                      </span>
                      <span className="text-xs text-muted-foreground truncate">
                        {candidate.snippet}
                      </span>
                      {isAttaching && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <Loader2 className="size-3 animate-spin" />
                          Attaching…
                        </span>
                      )}
                      {!isAttaching && alreadyLinked && (
                        <span className="text-xs text-emerald-600 mt-0.5">
                          Already attached to this job
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
